import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Loader2, 
  Search,
  Bell,
  Download,
  RefreshCw,
  Users,
  CheckCircle2,
  Clock,
  Activity,
  X,
  ChevronRight,
  FileText,
  Mail,
  Target,
  Shield,
  UserCheck,
  BarChart3,
  ArrowUpRight,
  Trophy,
  Building2,
  GraduationCap,
  Briefcase,
  Heart,
  Send,
  Calendar,
  LogOut,
  MessageSquare,
  BookOpen,
  Zap,
  Award,
  TrendingUp,
  Eye,
  Edit,
  Plus,
  Filter,
  MoreHorizontal,
  User,
  MapPin,
  Phone,
  Star,
  Clipboard,
  AlertCircle,
  Lock
} from "lucide-react";

// Segment colors - Development focused (NO RED)
const SEGMENT_COLORS = {
  "Builder": { bg: '#1B5E20', bgLight: '#E8F5E9', text: 'white' },
  "Accelerating": { bg: '#1565C0', bgLight: '#E3F2FD', text: 'white' },
  "Developing": { bg: '#E65100', bgLight: '#FFF3E0', text: 'white' },
  "Upskilling Priority": { bg: '#7C3AED', bgLight: '#F3E8FF', text: 'white' }
};

const ACTION_COLORS = {
  "Strategic Retention": { bg: '#1B5E20', text: 'white' },
  "Development Track": { bg: '#1565C0', text: 'white' },
  "Skills Alignment": { bg: '#E65100', text: 'white' },
  "Intensive Upskilling": { bg: '#7C3AED', text: 'white' }
};

// Regional HR Leaders Data
const HR_LEADERS = {
  "usa": {
    name: "Suzzette Chapman",
    title: "Regional HR Lead — US (SLIC) & Canada (ivari)",
    region: "North America",
    flags: "🇺🇸 🇨🇦",
    tagline: "Investing in Our Technology Talent Across North America",
    employeeCount: 82,
    departments: 2,
    businessUnits: ["US (SLIC)", "Canada (ivari)"]
  },
  "jamaica": {
    name: "Coretta Foster",
    title: "Regional HR Lead — Jamaica Technology",
    region: "Jamaica",
    flags: "🇯🇲",
    tagline: "Building Jamaica's Technology Future — One Developer at a Time",
    employeeCount: 242,
    departments: 6,
    businessUnits: ["Infrastructure & Technical Services", "Innovation & Data", "Business Optimization", "Application Services"]
  },
  "southern_caribbean": {
    name: "Shane Howell",
    title: "Regional HR Lead — Trinidad & Tobago, Barbados, Panama",
    region: "Southern Caribbean",
    flags: "🇹🇹 🇧🇧 🇵🇦",
    tagline: "Strengthening Caribbean Technology Capability",
    employeeCount: 100,
    departments: 3,
    businessUnits: ["Trinidad & Tobago", "Barbados", "Panama"]
  },
  "barbados": {
    name: "Shane Howell",
    title: "Regional HR Lead — Barbados",
    region: "Barbados",
    flags: "🇧🇧",
    tagline: "Strengthening Caribbean Technology Capability",
    employeeCount: 74,
    departments: 2,
    businessUnits: ["Technology Services", "Business Applications"]
  },
  "trinidad_tobago": {
    name: "Shane Howell",
    title: "Regional HR Lead — Trinidad & Tobago",
    region: "Trinidad & Tobago",
    flags: "🇹🇹",
    tagline: "Strengthening Caribbean Technology Capability",
    employeeCount: 24,
    departments: 2,
    businessUnits: ["Technology Services", "Business Applications"]
  },
  "panama": {
    name: "Shane Howell",
    title: "Regional HR Lead — Panama",
    region: "Panama",
    flags: "🇵🇦",
    tagline: "Strengthening Caribbean Technology Capability",
    employeeCount: 2,
    departments: 1,
    businessUnits: ["Technology Services"]
  }
};

// Segment Badge Component
const SegmentBadge = ({ segment }) => {
  if (!segment) return <span className="text-xs text-muted-foreground">—</span>;
  const colors = SEGMENT_COLORS[segment] || { bg: '#94a3b8', text: 'white' };
  return (
    <span 
      className="px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {segment}
    </span>
  );
};

// Action Badge Component
const ActionBadge = ({ action }) => {
  if (!action) return <span className="text-xs text-muted-foreground">—</span>;
  const colors = ACTION_COLORS[action] || { bg: '#94a3b8', text: 'white' };
  return (
    <span 
      className="px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {action}
    </span>
  );
};

// Donut Chart Component
const DonutChart = ({ data, size = 140, strokeWidth = 24 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  let currentOffset = 0;
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {data.map((item, index) => {
        const percentage = item.value / total;
        const dashLength = percentage * circumference;
        const dashOffset = currentOffset;
        currentOffset += dashLength;
        
        return (
          <circle
            key={index}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={item.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dashLength} ${circumference - dashLength}`}
            strokeDashoffset={-dashOffset}
            className="transition-all duration-500"
          />
        );
      })}
    </svg>
  );
};

export default function RegionalHRDashboard() {
  const navigate = useNavigate();
  const { region } = useParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  
  // API data states
  const [dashboardData, setDashboardData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [pipelineData, setPipelineData] = useState(null);
  const [managersData, setManagersData] = useState(null);
  const [programsData, setProgramsData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [communicationsData, setCommunicationsData] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
  });

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/hr/${region}/dashboard`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API}/hr/${region}/employees?limit=50`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

  const fetchPipelineData = async () => {
    try {
      const response = await fetch(`${API}/hr/${region}/pipeline`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setPipelineData(data);
      }
    } catch (error) {
      console.error("Error fetching pipeline data:", error);
    }
  };

  const fetchManagersData = async () => {
    try {
      const response = await fetch(`${API}/hr/${region}/managers`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setManagersData(data);
      }
    } catch (error) {
      console.error("Error fetching managers data:", error);
    }
  };

  const fetchProgramsData = async () => {
    try {
      const response = await fetch(`${API}/hr/${region}/programs`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setProgramsData(data);
      }
    } catch (error) {
      console.error("Error fetching programs data:", error);
    }
  };

  const fetchComplianceData = async () => {
    try {
      const response = await fetch(`${API}/hr/${region}/compliance`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setComplianceData(data);
      }
    } catch (error) {
      console.error("Error fetching compliance data:", error);
    }
  };

  const fetchCommunicationsData = async () => {
    try {
      const response = await fetch(`${API}/hr/${region}/communications/stats`, getAuthHeaders());
      if (response.ok) {
        const data = await response.json();
        setCommunicationsData(data);
      }
    } catch (error) {
      console.error("Error fetching communications data:", error);
    }
  };

  useEffect(() => {
    // Fetch all data on load
    fetchDashboardData();
    fetchEmployees();
    fetchPipelineData();
    fetchManagersData();
    fetchProgramsData();
    fetchComplianceData();
    fetchCommunicationsData();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [region]);

  // Get HR leader data - prefer API data, fallback to static
  const hrLeader = dashboardData?.hrLeader || HR_LEADERS[region] || HR_LEADERS["jamaica"];
  
  // Get stats from API or fallback
  const stats = dashboardData?.stats || {
    total: 0, completed: 0, inProgress: 0, notStarted: 0,
    completionRate: 0, participationRate: 0,
    validationsComplete: 0, validationsTotal: 0, devPlansCreated: 0
  };
  
  const segments = dashboardData?.segments || { Builder: 0, Accelerating: 0, Developing: 0, "Upskilling Priority": 0 };
  const departments = dashboardData?.departments || [];

  // Dummy employees data (will be replaced by API)
  const dummyEmployees = [];
  
  // Use API employees if available, otherwise fallback to dummy
  const displayEmployees = employees.length > 0 ? employees.map(emp => ({
    id: emp.id,
    name: emp.name,
    title: emp.title,
    manager: emp.manager,
    department: emp.department,
    segment: emp.segment,
    action: emp.developmentAction,
    programStatus: emp.programStatus,
    progress: emp.progress,
    nextMilestone: emp.nextMilestone
  })) : dummyEmployees;

  // Activity feed - show awaiting data if no real data
  const activityFeed = dashboardData?.recentActivity || [];

  // Use API managers data or show empty state
  const displayManagers = managersData?.managers || [];

  useEffect(() => {
    const token = localStorage.getItem("hrToken") || localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("hrToken");
    localStorage.removeItem("authToken");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F3F0]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[#7C3AED] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading HR Dashboard...</p>
        </div>
      </div>
    );
  }

  // Segment distribution data for donut chart - using API data
  const segmentData = [
    { label: 'Builder', value: segments.Builder || 0, color: '#1B5E20' },
    { label: 'Accelerating', value: segments.Accelerating || 0, color: '#1565C0' },
    { label: 'Developing', value: segments.Developing || 0, color: '#E65100' },
    { label: 'Upskilling Priority', value: segments["Upskilling Priority"] || 0, color: '#7C3AED' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F3F0]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#1a3a3a] text-white shadow-lg">
        <div className="max-w-[1600px] mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#C4A265] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">THCO</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-lg font-semibold">Workforce Intelligence</span>
                <Badge className="ml-2 bg-transparent border border-[#7C3AED] text-[#a78bfa] text-[10px] font-medium uppercase">
                  HR · {hrLeader.region}
                </Badge>
              </div>
            </div>

            {/* Center - Search */}
            <div className="flex-1 max-w-md hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                <Input
                  placeholder="Search by name, manager, department, development status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-[#2a4a4a] border-white/20 text-white placeholder:text-white/50 focus:bg-[#2a4a4a] h-9"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/30">⌘K</span>
              </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" className="h-9 border-white/20 text-white hover:bg-white/10 gap-2">
                <Download className="w-4 h-4" />
                Export
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 text-white/70 hover:text-white hover:bg-white/10 relative"
              >
                <Bell className="w-4 h-4" />
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#7C3AED] rounded-full text-[10px] flex items-center justify-center">
                  3
                </span>
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#7C3AED] rounded-full flex items-center justify-center text-sm font-semibold">
                  {hrLeader.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="hidden lg:block text-right">
                  <p className="text-sm font-medium leading-none">{hrLeader.name}</p>
                  <p className="text-xs text-white/60">Regional HR</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-white/70 hover:text-white hover:bg-white/10"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-12 bg-transparent border-0 gap-1 w-full justify-start overflow-x-auto">
              {[
                { value: "overview", label: "Regional Overview" },
                { value: "pipeline", label: "Development Pipeline" },
                { value: "communications", label: "Communications" },
                { value: "enablement", label: "Manager Enablement" },
                { value: "programs", label: "Regional Programs" },
                { value: "compliance", label: "Compliance & Participation" },
                { value: "profiles", label: "Employee Profiles" }
              ].map(tab => (
                <TabsTrigger 
                  key={tab.value}
                  value={tab.value} 
                  className="data-[state=active]:bg-[#1a3a3a] data-[state=active]:text-white data-[state=active]:shadow-none data-[state=active]:border-b-[3px] data-[state=active]:border-[#7C3AED] rounded-t-md px-4 text-[13px] font-semibold whitespace-nowrap"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          
          {/* TAB 1: REGIONAL OVERVIEW */}
          <TabsContent value="overview" className="mt-0 space-y-6">
            {/* Welcome Banner */}
            <Card className="bg-gradient-to-r from-[#1a3a3a] to-[#2a4a4a] text-white border-0 shadow-lg">
              <CardContent className="py-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{hrLeader.flags}</span>
                      <span className="text-sm font-medium text-white/70 uppercase tracking-wider">
                        {hrLeader.region} Technology Assessment
                      </span>
                    </div>
                    <h1 className="text-2xl font-bold mb-1">Welcome, {hrLeader.name.split(' ')[0]}</h1>
                    <p className="text-white/70 text-sm mb-3">{hrLeader.title}</p>
                    <p className="text-[#C4A265] italic text-sm">"{hrLeader.tagline}"</p>
                    <div className="flex items-center gap-4 mt-4 text-sm text-white/60">
                      <span>{hrLeader.employeeCount} Technology Professionals</span>
                      <span>·</span>
                      <span>{hrLeader.departments} {region === "southern_caribbean" ? "Territories" : "Departments"}</span>
                      <span>·</span>
                      <span>4 Assessment Phases</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-white/50">Last updated</p>
                    <p className="text-sm text-white/70">{lastUpdated.toLocaleTimeString()}</p>
                    <Button variant="ghost" size="icon" className="mt-2 text-white/50 hover:text-white">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#1a3a3a]/10 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#1a3a3a]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Technology Professionals</p>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.completed}</p>
                  <p className="text-sm text-muted-foreground">{stats.completionRate}% Complete</p>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.inProgress || 0}</p>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.notStarted || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-2">
                    <UserCheck className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">0/0</p>
                  <p className="text-sm text-muted-foreground">Manager Validations</p>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                    <Target className="w-5 h-5 text-blue-600" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.participationRate || 0}%</p>
                  <p className="text-sm text-muted-foreground">Participation Rate</p>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <div className="w-10 h-10 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center mb-2">
                    <FileText className="w-5 h-5 text-[#7C3AED]" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{stats.devPlansCreated || 0}</p>
                  <p className="text-sm text-muted-foreground">Development Plans</p>
                </CardContent>
              </Card>
            </div>

            {/* Development Investment Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Talent Distribution */}
              <Card className="bg-white shadow-sm border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-[#7C3AED]" />
                    Talent Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(segments.Builder || 0) + (segments.Accelerating || 0) + (segments.Developing || 0) + (segments["Upskilling Priority"] || 0) > 0 ? (
                    <>
                      {/* Stacked Bar */}
                      <div className="h-8 rounded-lg overflow-hidden flex mb-4">
                        {segmentData.map((item, i) => (
                          <div 
                            key={i}
                            style={{ width: `${(item.value / stats.total) * 100}%`, backgroundColor: item.color }}
                            className="flex items-center justify-center text-white text-xs font-medium"
                          >
                            {item.value > 15 && item.value}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-[#E8F5E9] rounded-lg text-center">
                          <p className="text-xl font-bold text-[#1B5E20]">{segments.Builder || 0}</p>
                          <p className="text-xs text-[#1B5E20]">Builders</p>
                          <p className="text-[10px] text-[#1B5E20]/70">Ready to Lead</p>
                        </div>
                        <div className="p-3 bg-[#E3F2FD] rounded-lg text-center">
                          <p className="text-xl font-bold text-[#1565C0]">{segments.Accelerating || 0}</p>
                          <p className="text-xs text-[#1565C0]">Accelerating</p>
                          <p className="text-[10px] text-[#1565C0]/70">Growth Track</p>
                        </div>
                        <div className="p-3 bg-[#FFF3E0] rounded-lg text-center">
                          <p className="text-xl font-bold text-[#E65100]">{segments.Developing || 0}</p>
                          <p className="text-xs text-[#E65100]">Developing</p>
                          <p className="text-[10px] text-[#E65100]/70">Foundation</p>
                        </div>
                        <div className="p-3 bg-[#F3E8FF] rounded-lg text-center">
                          <p className="text-xl font-bold text-[#7C3AED]">{segments["Upskilling Priority"] || 0}</p>
                          <p className="text-xs text-[#7C3AED]">Upskilling Priority</p>
                          <p className="text-[10px] text-[#7C3AED]/70">Priority Investment</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <Clock className="w-6 h-6 text-slate-400" />
                      </div>
                      <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                      <p className="text-xs text-muted-foreground mt-1">Talent segments will appear once assessments are completed</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Development Action Pipeline */}
              <Card className="bg-white shadow-sm border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Zap className="w-4 h-4 text-[#7C3AED]" />
                    Development Action Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <Clock className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Pipeline data will appear once assessments are completed and actions are assigned</p>
                  </div>
                </CardContent>
              </Card>

              {/* Regional Investment Summary */}
              <Card className="bg-white shadow-sm border-0">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#7C3AED]" />
                    Development Investment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <Clock className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Development investment data will appear once assessments are completed</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Department Progress */}
            <Card className="bg-white shadow-sm border-0">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#7C3AED]" />
                    Assessment Progress by {region === "southern_caribbean" ? "Territory" : "Department"}
                  </CardTitle>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Mail className="w-4 h-4" />
                    Send Reminder to Lagging Teams
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">{region === "southern_caribbean" ? "Territory" : "Department"}</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Head</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Headcount</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Complete</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">In Progress</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Not Started</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments.map((dept, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50 cursor-pointer">
                          <td className="p-3 text-sm font-medium">{dept.name}</td>
                          <td className="p-3 text-sm text-muted-foreground">{dept.head}</td>
                          <td className="p-3 text-sm text-center">{dept.count}</td>
                          <td className="p-3 text-sm text-center text-emerald-600 font-semibold">{dept.completed}</td>
                          <td className="p-3 text-sm text-center text-amber-600">{dept.inProgress}</td>
                          <td className="p-3 text-sm text-center text-slate-400">{dept.notStarted}</td>
                          <td className="p-3 text-center">
                            <Badge className={`${
                              dept.rate > 70 ? 'bg-emerald-100 text-emerald-700' :
                              dept.rate > 50 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {dept.rate.toFixed(1)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Activity Feed */}
            <Card className="bg-white shadow-sm border-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#7C3AED]" />
                  HR Activity Feed
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activityFeed.length > 0 ? (
                  <div className="space-y-3">
                    {activityFeed.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          item.type === 'completion' ? 'bg-emerald-500' :
                          item.type === 'training' ? 'bg-blue-500' :
                          item.type === 'communication' ? 'bg-purple-500' :
                          item.type === 'milestone' ? 'bg-amber-500' : 'bg-slate-400'
                        }`} />
                        <div className="flex-1">
                          <p className="text-sm text-slate-700">{item.message}</p>
                          <p className="text-xs text-muted-foreground">{item.time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                      <Clock className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Activity will appear as assessments progress</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 2: DEVELOPMENT PIPELINE */}
          <TabsContent value="pipeline" className="mt-0 space-y-6">
            {/* Filters */}
            <Card className="bg-white shadow-sm border-0">
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Select defaultValue="all">
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Segment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Segments</SelectItem>
                      <SelectItem value="builder">Builder</SelectItem>
                      <SelectItem value="accelerating">Accelerating</SelectItem>
                      <SelectItem value="developing">Developing</SelectItem>
                      <SelectItem value="upskilling">Upskilling Priority</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select defaultValue="all">
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Development Action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="retention">Strategic Retention</SelectItem>
                      <SelectItem value="track">Development Track</SelectItem>
                      <SelectItem value="alignment">Skills Alignment</SelectItem>
                      <SelectItem value="intensive">Intensive Upskilling</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select defaultValue="all">
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="Program Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="not-enrolled">Not Enrolled</SelectItem>
                      <SelectItem value="enrolled">Enrolled</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="on-track">On Track</SelectItem>
                    </SelectContent>
                  </Select>

                  <div className="flex-1" />

                  <Button variant="outline" size="sm" className="gap-2">
                    <Filter className="w-4 h-4" />
                    Save Filter
                  </Button>
                  <Button variant="outline" size="sm" className="gap-2">
                    <Download className="w-4 h-4" />
                    Export
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Development Funnel */}
            <Card className="bg-white shadow-sm border-0">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Development Funnel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { stage: "Assessed", count: 0, pct: 0 },
                    { stage: "Development Plan Assigned", count: 0, pct: 0 },
                    { stage: "Training Enrolled", count: 0, pct: 0 },
                    { stage: "Actively in Training", count: 0, pct: 0 },
                    { stage: "Milestone Achieved", count: 0, pct: 0 }
                  ].map((stage, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-48 text-sm font-medium text-slate-700">{stage.stage}</div>
                      <div className="flex-1">
                        <div className="h-8 bg-slate-100 rounded-lg overflow-hidden flex items-center pl-3">
                          <span className="text-sm text-slate-400">{stage.count}</span>
                        </div>
                      </div>
                      <div className="w-16 text-sm text-right text-muted-foreground">({stage.pct}%)</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground text-center mt-4">Funnel data will populate once assessments are completed and development plans are assigned</p>
              </CardContent>
            </Card>

            {/* Employee Table */}
            <Card className="bg-white shadow-sm border-0">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-4 text-xs font-semibold text-slate-600">Employee</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-600">Manager</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-600">Segment</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-600">Dev. Action</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-600">Status</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-600">Progress</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-600">Next Milestone</th>
                        <th className="text-left p-4 text-xs font-semibold text-slate-600"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEmployees.map((emp) => (
                        <tr key={emp.id} className="border-b hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-[#1a3a3a] rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {emp.name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{emp.name}</p>
                                <p className="text-xs text-muted-foreground">{emp.title}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-slate-600">{emp.manager}</td>
                          <td className="p-4"><span className="text-sm text-slate-400">—</span></td>
                          <td className="p-4"><span className="text-sm text-slate-400">—</span></td>
                          <td className="p-4">
                            <Badge className={`text-xs ${
                              emp.progress > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                            }`}>
                              {emp.progress > 0 ? 'In Progress' : 'Not Started'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="w-24">
                              <Progress value={emp.progress} className="h-2" />
                              <span className="text-xs text-muted-foreground">{emp.progress}%</span>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-slate-400">—</td>
                          <td className="p-4">
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-white shadow-sm border-0 border-l-4 border-l-[#1B5E20]">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-[#1B5E20]" />
                    <span className="font-semibold text-[#1B5E20]">STRATEGIC RETENTION</span>
                    <span className="ml-auto font-bold">{segments.Builder || 0}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Recognition Program</p>
                    <p>● Enrolled: 8</p>
                    <p>● Pending discussion: 4</p>
                  </div>
                  <Button variant="link" className="p-0 h-auto mt-3 text-[#1B5E20]">View All →</Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 border-l-4 border-l-[#1565C0]">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-[#1565C0]" />
                    <span className="font-semibold text-[#1565C0]">DEVELOPMENT TRACK</span>
                    <span className="ml-auto font-bold">{segments.Accelerating || 0}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Training Enrollment</p>
                    <p>● Active: 22</p>
                    <p>● On track: 18 (82%)</p>
                  </div>
                  <Button variant="link" className="p-0 h-auto mt-3 text-[#1565C0]">View All →</Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 border-l-4 border-l-[#E65100]">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-[#E65100]" />
                    <span className="font-semibold text-[#E65100]">SKILLS ALIGNMENT</span>
                    <span className="ml-auto font-bold">{Math.round(stats.developing * 0.5)}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Role Exploration</p>
                    <p>● Discovery: 12</p>
                    <p>● Target identified: 8</p>
                  </div>
                  <Button variant="link" className="p-0 h-auto mt-3 text-[#E65100]">View All →</Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 border-l-4 border-l-[#7C3AED]">
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-[#7C3AED]" />
                    <span className="font-semibold text-[#7C3AED]">INTENSIVE UPSKILLING</span>
                    <span className="ml-auto font-bold">{segments["Upskilling Priority"] || 0}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Bootcamp Program</p>
                    <p>● Enrolled: 18</p>
                    <p>● Showing improvement: 12</p>
                  </div>
                  <Button variant="link" className="p-0 h-auto mt-3 text-[#7C3AED]">View All →</Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TAB 3: COMMUNICATIONS */}
          <TabsContent value="communications" className="mt-0 space-y-6">
            {/* Communication KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-slate-900">24</p>
                  <p className="text-sm text-muted-foreground">Sent This Month</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-emerald-600">78%</p>
                  <p className="text-sm text-muted-foreground">Average Open Rate</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-slate-900">5</p>
                  <p className="text-sm text-muted-foreground">Scheduled</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-slate-900">12</p>
                  <p className="text-sm text-muted-foreground">Templates Active</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white shadow-sm border-0 hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-5 text-center">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-[#7C3AED]" />
                  <p className="font-semibold">Remind Not Started</p>
                  <p className="text-sm text-muted-foreground">{stats.notStarted} employees</p>
                  <Button className="w-full mt-3 bg-[#7C3AED] hover:bg-[#7C3AED]/90">Send</Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-5 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="font-semibold">Team Update</p>
                  <p className="text-sm text-muted-foreground">{stats.total} employees</p>
                  <Button variant="outline" className="w-full mt-3">Send</Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-5 text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-amber-600" />
                  <p className="font-semibold">Town Hall Invite</p>
                  <p className="text-sm text-muted-foreground">{stats.total} employees</p>
                  <Button variant="outline" className="w-full mt-3">Send</Button>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-sm border-0 hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="pt-5 text-center">
                  <UserCheck className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                  <p className="font-semibold">Manager Reminder</p>
                  <p className="text-sm text-muted-foreground">{displayManagers.length} managers</p>
                  <Button variant="outline" className="w-full mt-3">Send</Button>
                </CardContent>
              </Card>
            </div>

            {/* Communication Composer */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <Card className="bg-white shadow-sm border-0 lg:col-span-3">
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Compose Communication</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <Select defaultValue="all">
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Recipients" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Employees</SelectItem>
                        <SelectItem value="not-started">Not Started</SelectItem>
                        <SelectItem value="in-progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select defaultValue="all">
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Segment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Segments</SelectItem>
                        <SelectItem value="builder">Builders</SelectItem>
                        <SelectItem value="accelerating">Accelerating</SelectItem>
                        <SelectItem value="developing">Developing</SelectItem>
                        <SelectItem value="upskilling">Upskilling</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-[#7C3AED] font-medium">This message will reach <strong>{stats.total}</strong> employees</p>
                  
                  <div>
                    <Input placeholder="Subject line..." className="mb-3" />
                    <Textarea 
                      placeholder="Write your message here... Use merge fields like {{first_name}}, {{manager_name}}, {{assessment_link}}"
                      rows={6}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button className="bg-[#1a3a3a] hover:bg-[#1a3a3a]/90 gap-2">
                      <Send className="w-4 h-4" />
                      Send Now
                    </Button>
                    <Button variant="outline" className="gap-2">
                      <Calendar className="w-4 h-4" />
                      Schedule
                    </Button>
                    <Button variant="ghost">Save Draft</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Template Library */}
              <Card className="bg-white shadow-sm border-0 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base font-semibold">Template Library</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {[
                        { name: "Welcome & Invitation", category: "Assessment" },
                        { name: "Reminder — Not Started", category: "Assessment" },
                        { name: "Phase Completion Thank You", category: "Assessment" },
                        { name: "Development Plan Available", category: "Development" },
                        { name: "Training Program Enrollment", category: "Development" },
                        { name: "Milestone Achievement", category: "Development" },
                        { name: "Manager Validation Request", category: "Manager" },
                        { name: "Manager Briefing Invite", category: "Manager" },
                        { name: "Town Hall Invitation", category: "Events" }
                      ].map((template, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer">
                          <div>
                            <p className="font-medium text-sm">{template.name}</p>
                            <p className="text-xs text-muted-foreground">{template.category}</p>
                          </div>
                          <Button variant="ghost" size="sm">Use</Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <Button variant="outline" className="w-full mt-3 gap-2">
                    <Plus className="w-4 h-4" />
                    Create Template
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Communication History */}
            <Card className="bg-white shadow-sm border-0">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Communication History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Date Sent</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Subject</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Recipients</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Segment</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Open Rate</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Click Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { date: "Jan 28", subject: "Development Plan Preview Available", recipients: 45, segment: "Accelerating", openRate: 82, clickRate: 71 },
                        { date: "Jan 25", subject: "Technical Assessment Now Open", recipients: stats.total, segment: "All", openRate: 78, clickRate: 65 },
                        { date: "Jan 20", subject: "Your Technology Assessment Journey Begins", recipients: stats.total, segment: "All", openRate: 91, clickRate: 84 }
                      ].map((comm, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50 cursor-pointer">
                          <td className="p-3 text-sm">{comm.date}</td>
                          <td className="p-3 text-sm font-medium">{comm.subject}</td>
                          <td className="p-3 text-sm">{comm.recipients}</td>
                          <td className="p-3 text-sm">{comm.segment}</td>
                          <td className="p-3">
                            <Badge className={`${comm.openRate > 75 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {comm.openRate}%
                            </Badge>
                          </td>
                          <td className="p-3 text-sm">{comm.clickRate}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: MANAGER ENABLEMENT */}
          <TabsContent value="enablement" className="mt-0 space-y-6">
            {/* Manager Readiness KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-slate-900">{displayManagers.filter(m => m.enablement?.briefed).length}/{displayManagers.length}</p>
                  <p className="text-sm text-muted-foreground">Managers Briefed</p>
                  <Badge className="mt-2 bg-slate-100 text-slate-600">Awaiting data</Badge>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-slate-900">{displayManagers.filter(m => m.enablement?.materials).length}</p>
                  <p className="text-sm text-muted-foreground">Guides Downloaded</p>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-slate-900">{displayManagers.filter(m => m.enablement?.coaching).length}/{displayManagers.length}</p>
                  <p className="text-sm text-muted-foreground">Coaching Completed</p>
                  <Badge className="mt-2 bg-slate-100 text-slate-600">—</Badge>
                </CardContent>
              </Card>
              <Card className="bg-white shadow-sm border-0">
                <CardContent className="pt-5">
                  <p className="text-3xl font-bold text-slate-900">24</p>
                  <p className="text-sm text-muted-foreground">1:1 Templates Accessed</p>
                </CardContent>
              </Card>
            </div>

            {/* Manager Roster */}
            <Card className="bg-white shadow-sm border-0">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Manager Roster</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Manager</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Title</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Department</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Direct Reports</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Briefed</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Materials</th>
                        <th className="text-center p-3 text-xs font-semibold text-slate-600">Coaching</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayManagers.map((mgr, i) => (
                        <tr key={i} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-[#1a3a3a] rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {mgr.name.split(' ').map(n => n[0]).join('')}
                              </div>
                              <span className="font-medium">{mgr.name}</span>
                            </div>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{mgr.title}</td>
                          <td className="p-3 text-sm">{mgr.department}</td>
                          <td className="p-3 text-sm text-center">{mgr.directReports || mgr.reports}</td>
                          <td className="p-3 text-center">{mgr.enablement?.briefed || mgr.briefed ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <Clock className="w-5 h-5 text-amber-500 mx-auto" />}</td>
                          <td className="p-3 text-center">{mgr.enablement?.materials || mgr.materials ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <Clock className="w-5 h-5 text-amber-500 mx-auto" />}</td>
                          <td className="p-3 text-center">{mgr.enablement?.coaching || mgr.coaching ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <Clock className="w-5 h-5 text-amber-500 mx-auto" />}</td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              {!(mgr.enablement?.briefed || mgr.briefed) && <Button size="sm" variant="outline">Brief</Button>}
                              {!(mgr.enablement?.materials || mgr.materials) && <Button size="sm" variant="outline">Send Materials</Button>}
                              <Button size="sm" variant="ghost">View Team</Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Toolkit Library */}
            <Card className="bg-white shadow-sm border-0">
              <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[#7C3AED]" />
                  Manager Toolkit Library
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { title: "Development Conversation Guide", category: "Conversations", icon: MessageSquare },
                    { title: "Framing Assessment as Investment", category: "Talking Points", icon: Target },
                    { title: "Skills Discovery Template", category: "Alignment", icon: Search },
                    { title: "Monthly 1:1 Check-In Template", category: "Coaching", icon: Calendar },
                    { title: "GROW Model Framework", category: "Coaching", icon: TrendingUp },
                    { title: "Recognition Best Practices", category: "Recognition", icon: Award }
                  ].map((resource, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
                      <div className="w-10 h-10 bg-[#7C3AED]/10 rounded-lg flex items-center justify-center">
                        <resource.icon className="w-5 h-5 text-[#7C3AED]" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{resource.title}</p>
                        <p className="text-xs text-muted-foreground">{resource.category}</p>
                      </div>
                      <Button variant="ghost" size="icon">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 5: REGIONAL PROGRAMS */}
          <TabsContent value="programs" className="mt-0 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Builder Recognition */}
              <Card className="bg-white shadow-sm border-0">
                <CardHeader className="border-b bg-[#E8F5E9]">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-[#1B5E20]">
                    <Trophy className="w-5 h-5" />
                    Builder Recognition Program
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  <p className="text-sm text-muted-foreground">Recognition and retention for top performers</p>
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Clock className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Program enrollment will begin once assessments are completed</p>
                  </div>
                  <Button className="w-full bg-[#1B5E20] hover:bg-[#1B5E20]/90" disabled>Manage Program</Button>
                </CardContent>
              </Card>

              {/* Tech Academy */}
              <Card className="bg-white shadow-sm border-0">
                <CardHeader className="border-b bg-[#E3F2FD]">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-[#1565C0]">
                    <GraduationCap className="w-5 h-5" />
                    Technology Academy
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  <p className="text-sm text-muted-foreground">Multi-track technical development</p>
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Clock className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Track enrollment will begin once assessments are completed</p>
                  </div>
                  <Button className="w-full bg-[#1565C0] hover:bg-[#1565C0]/90" disabled>Manage Program</Button>
                </CardContent>
              </Card>

              {/* Intensive Upskilling */}
              <Card className="bg-white shadow-sm border-0">
                <CardHeader className="border-b bg-[#F3E8FF]">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-[#7C3AED]">
                    <Zap className="w-5 h-5" />
                    Intensive Upskilling Bootcamp
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5 space-y-4">
                  <p className="text-sm text-muted-foreground">Accelerated foundational skill building</p>
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Clock className="w-8 h-8 text-slate-400 mb-2" />
                    <p className="text-sm font-medium text-slate-600">Awaiting Data</p>
                    <p className="text-xs text-muted-foreground mt-1">Bootcamp enrollment will begin once assessments are completed</p>
                  </div>
                  <Button className="w-full bg-[#7C3AED] hover:bg-[#7C3AED]/90" disabled>Manage Program</Button>
                </CardContent>
              </Card>
            </div>

            {/* Upcoming */}
            <Card className="bg-gradient-to-r from-[#7C3AED]/10 to-transparent border-0">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#7C3AED]" />
                  <span className="font-medium">Programs:</span>
                  <span className="text-muted-foreground">Programs will be available once assessment data is analyzed</span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 6: COMPLIANCE & PARTICIPATION */}
          <TabsContent value="compliance" className="mt-0 space-y-6">
            {region !== "usa" ? (
              <>
                {/* Voluntary Participation - Caribbean */}
                <Card className="bg-white shadow-sm border-0">
                  <CardHeader className="border-b bg-[#F3E8FF]">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#7C3AED]" />
                      Voluntary Participation Compliance
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-5">
                    <div className="mb-4">
                      <div className="flex justify-between mb-2">
                        <span className="font-medium">{hrLeader.region} Assessment Participation</span>
                        <span className="font-bold text-[#7C3AED]">{stats.participationRate || 0}%</span>
                      </div>
                      <Progress value={stats.avgParticipation} className="h-3" />
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold">{stats.total}</p>
                        <p className="text-sm text-muted-foreground">Invited</p>
                      </div>
                      <div className="p-4 bg-emerald-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-600">{Math.round(stats.total * stats.avgParticipation / 100)}</p>
                        <p className="text-sm text-emerald-600">Opted In ({stats.participationRate || 0}%)</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg text-center">
                        <p className="text-2xl font-bold">{Math.round(stats.total * 0.033)}</p>
                        <p className="text-sm text-muted-foreground">Declined (3.3%)</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{Math.round(stats.total * 0.025)}</p>
                        <p className="text-sm text-amber-600">No Response</p>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        <span className="font-semibold text-emerald-700">Workplace Organization Consultation: COMPLETED</span>
                      </div>
                      <p className="text-sm text-emerald-600">Consultation Date: December 18, 2025</p>
                      <div className="flex gap-2 mt-3">
                        <Button variant="outline" size="sm">View Meeting Notes</Button>
                        <Button variant="outline" size="sm">View Agreement</Button>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-4">
                      <Button variant="outline" className="gap-2">
                        <Download className="w-4 h-4" />
                        Download Consent Records
                      </Button>
                      <Button variant="outline" className="gap-2">
                        <Mail className="w-4 h-4" />
                        Send Reminder to No Response
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Labor Law Reference */}
                <Card className="bg-white shadow-sm border-0">
                  <CardHeader>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      {hrLeader.flags} {hrLeader.region} Employment Compliance Reference
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="font-semibold mb-2">Assessment Participation</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• Voluntary participation required</li>
                          <li>• Union consultation completed</li>
                          <li>• Individual consent documented</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="font-semibold mb-2">Development Programs</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• Training = working hours</li>
                          <li>• Equal access required</li>
                          <li>• Plans must be documented</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="font-semibold mb-2">Data Protection</p>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• Personal information classified</li>
                          <li>• 7 year retention period</li>
                          <li>• Employee access rights apply</li>
                        </ul>
                      </div>
                    </div>
                    <Button variant="link" className="mt-4 text-[#7C3AED]">Download Full Compliance Guide →</Button>
                  </CardContent>
                </Card>
              </>
            ) : (
              /* North America - Standard */
              <Card className="bg-white shadow-sm border-0">
                <CardHeader className="border-b bg-emerald-50">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-emerald-700">
                    <CheckCircle2 className="w-5 h-5" />
                    Assessment Participation
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-5">
                  <p className="text-muted-foreground mb-4">Standard assessment protocol — no special compliance requirements</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span>US (SLIC)</span>
                      <Badge className="bg-emerald-100 text-emerald-700">27 of 27 (100%)</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span>Canada (ivari)</span>
                      <Badge className="bg-emerald-100 text-emerald-700">55 of 55 (100%)</Badge>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">82 of 82 (100%)</p>
                    <p className="text-sm text-emerald-600">Total North America Participation</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Audit Trail */}
            <Card className="bg-white shadow-sm border-0">
              <CardHeader>
                <CardTitle className="text-base font-semibold">Audit Trail</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Timestamp</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">User</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Action</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { time: "Jan 28, 3:45 PM", user: hrLeader.name, action: "Viewed Employee Profile", details: "Sarah Mitchell" },
                        { time: "Jan 28, 3:30 PM", user: hrLeader.name, action: "Sent Communication", details: "Phase 2 Reminder to 45 employees" },
                        { time: "Jan 28, 2:00 PM", user: hrLeader.name, action: "Downloaded Records", details: `${hrLeader.region} Q1 export` },
                        { time: "Jan 28, 11:00 AM", user: hrLeader.name, action: "Updated Status", details: "Employee → In Progress" }
                      ].map((log, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-3 text-sm text-muted-foreground">{log.time}</td>
                          <td className="p-3 text-sm">{log.user}</td>
                          <td className="p-3 text-sm">{log.action}</td>
                          <td className="p-3 text-sm text-muted-foreground">{log.details}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 7: EMPLOYEE PROFILES */}
          <TabsContent value="profiles" className="mt-0 space-y-6">
            <Card className="bg-white shadow-sm border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Employee Directory</CardTitle>
                  <div className="flex gap-2">
                    <Input placeholder="Search employees..." className="w-64" />
                    <Select defaultValue="all">
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Segment" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Segments</SelectItem>
                        <SelectItem value="builder">Builder</SelectItem>
                        <SelectItem value="accelerating">Accelerating</SelectItem>
                        <SelectItem value="developing">Developing</SelectItem>
                        <SelectItem value="upskilling">Upskilling Priority</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {displayEmployees.slice(0, 6).map((emp) => (
                    <Card 
                      key={emp.id} 
                      className="border hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => {
                        setSelectedEmployee(emp);
                        setShowEmployeeModal(true);
                      }}
                    >
                      <CardContent className="pt-5">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-12 h-12 bg-[#1a3a3a] rounded-full flex items-center justify-center text-white font-semibold">
                            {emp.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold">{emp.name}</p>
                            <p className="text-sm text-muted-foreground">{emp.title}</p>
                            <p className="text-xs text-muted-foreground">{emp.department}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <SegmentBadge segment={emp.segment} />
                          <ActionBadge action={emp.action} />
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-semibold">{emp.progress}%</span>
                        </div>
                        <Progress value={emp.progress} className="h-2 mt-1" />
                        <Button 
                          variant="ghost" 
                          className="w-full mt-3 text-[#7C3AED]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEmployee(emp);
                            setShowEmployeeModal(true);
                          }}
                        >
                          View Profile <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-4 px-6">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between text-xs text-muted-foreground">
          <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
          <span>© 2026 THCO · HR Intelligence Platform · {hrLeader.region}</span>
        </div>
      </footer>

      {/* Employee Profile Modal */}
      <Dialog open={showEmployeeModal} onOpenChange={setShowEmployeeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedEmployee && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#1a3a3a] rounded-full flex items-center justify-center text-white text-xl font-semibold">
                    {selectedEmployee.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedEmployee.name}</h2>
                    <p className="text-sm text-muted-foreground">{selectedEmployee.title}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-slate-500">Awaiting Assessment</Badge>
                    </div>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* Left Column - Employee Info */}
                <div className="space-y-4">
                  {/* Basic Info Card */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <User className="w-4 h-4 text-[#7C3AED]" />
                        Employee Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Department</p>
                          <p className="text-sm font-medium">{selectedEmployee.department}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Manager</p>
                          <p className="text-sm font-medium">{selectedEmployee.manager}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Location</p>
                          <p className="text-sm font-medium">{hrLeader.region}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-xs text-muted-foreground">Email</p>
                          <p className="text-sm font-medium">{selectedEmployee.name.toLowerCase().replace(' ', '.')}@sagicor.com</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Assessment Progress Card */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Target className="w-4 h-4 text-[#7C3AED]" />
                        Assessment Progress
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="text-sm">Overall Progress</span>
                            <span className="text-sm font-bold">{selectedEmployee.progress}%</span>
                          </div>
                          <Progress value={selectedEmployee.progress} className="h-3" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground">BRI Score</p>
                            <p className="text-xl font-bold text-slate-400">—</p>
                            <p className="text-[10px] text-muted-foreground">Awaiting data</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground">Segment</p>
                            <p className="text-xl font-bold text-slate-400">—</p>
                            <p className="text-[10px] text-muted-foreground">Awaiting data</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {['Self-Assessment', 'Technical', 'Manager Validation', 'Hands-On'].map((phase, i) => (
                            <div key={i} className="flex items-center gap-2">
                              {selectedEmployee.progress > (i + 1) * 20 ? (
                                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              ) : selectedEmployee.progress > i * 20 ? (
                                <Clock className="w-4 h-4 text-amber-500" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                              )}
                              <span className="text-sm">{phase}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Development Plan */}
                <div className="space-y-4">
                  {/* Development Plan Card */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2 bg-[#F3E8FF] rounded-t-lg">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-[#7C3AED]">
                        <Clipboard className="w-4 h-4" />
                        Development Plan
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Lock className="w-10 h-10 text-slate-300 mb-3" />
                        <p className="text-sm font-medium text-slate-600">Awaiting Assessment Data</p>
                        <p className="text-xs text-muted-foreground mt-2 max-w-[200px]">Development plan will be generated once assessment is completed and reviewed by admin</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* HR Notes Card */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#7C3AED]" />
                        HR Notes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Textarea 
                        placeholder="Add private HR notes about this employee's development journey..."
                        className="min-h-[80px] resize-none"
                      />
                      <p className="text-xs text-muted-foreground mt-2">Notes are private to HR and not visible to managers or employees.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <div className="flex gap-2">
                  <Button variant="outline" className="gap-2">
                    <Mail className="w-4 h-4" />
                    Send Communication
                  </Button>
                  <Button variant="outline" className="gap-2">
                    <Calendar className="w-4 h-4" />
                    Schedule Meeting
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="gap-2">
                    <Download className="w-4 h-4" />
                    Export Profile
                  </Button>
                  <Button className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 gap-2" disabled>
                    <Edit className="w-4 h-4" />
                    Edit Development Plan
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
